import axios from 'axios';
import { cachetime } from '../config';
import { getSightDistance } from './sightmap';
import { distanceFromSightToPoi } from '@/utils/map';
import poi from '../modules/poi';

const defaultFilter = {
  categories: [], 
  districts: [], 
  cuisines: []
};

const PAGE_SIZE = 10;

export default function() {
  return {
    namespaced: true,
    state: {
      recommends: {},
      filter: {},
      qtsasFilter: {},
      qtsasSearch: {
        locale: '',
        category: '',
        district: '',
        cuisine: '',
        keyword: '',
        resultCount: 0,
        results: []
      },
      qtsasRestaurants: {},
      restaurantFilter: {},
      search: {
        locale: '',
        category: '',
        cuisine: '',
        keyword: '',
        resultCount: 0,
        results: []
      },
      restaurants: {},
      __updatedAt: {},
    },
    mutations: {
      SET_RECOMMENDS(state, { data, locale}) {
        state.recommends = {
          ...state.recommends,
          [locale]: data,
        }
        state.__updatedAt[locale] = Date.now();
      },
      SET_FILTER(state, { filter, data, locale }) {
        state[filter] = {
          ...state[filter],
          [locale]: {
            data,
            __updatedAt: Date.now()
          },
        }
      },
      RESET_QTSAS_RESTAURANT_SEARCH(state, { 
        category, district, cuisine, keyword, locale
      }) {
        state.qtsasSearch = {
          category, district, cuisine, keyword, locale,
          resultCount: 0,
          results: []
        }
      },
      REFRESH_QTSAS_RESTAURANT_SEARCH_RESULT(state, { resultCount }) {
        state.qtsasSearch = {
          ...state.qtsasSearch,
          resultCount,
          results: Array.apply(null, Array(resultCount))
        }
      },
      UPDATE_QTSAS_RESTAURANT_SEARCH_RESULT(state, { page=1, results }) {
        const START_IDX = (page - 1) * PAGE_SIZE;
        results.forEach((el, idx) => {
          state.qtsasSearch.results[START_IDX + idx] = {
            ...el
          };
        });
      },
      RESET_RESTAURANT_SEARCH(state, { cuisine, category, keyword, locale}) {
        state.search = {
          cuisine, category, keyword, locale,
          resultCount: 0,
          results: []
        }
      },
      REFRESH_RESTAURANT_SEARCH_RESULT(state, { resultCount }) {
        state.search = {
          ...state.search,
          resultCount,
          results: Array.apply(null, Array(resultCount))
        }
      },
      UPDATE_RESTAURANT_SEARCH_RESULT(state, { page=1, results }) {
        const START_IDX = (page - 1) * PAGE_SIZE;
        results.forEach((el, idx) => {
          state.search.results[START_IDX + idx] = {
            ...el
          };
        });
      },
      UPDATE_QTSAS_RESTAURANT(state, { data, id, locale }) {
        let allRestaurant = state.qtsasRestaurants[id] || {};
        state.qtsasRestaurants = {
          ...state.qtsasRestaurants,
          [id]: {
            ...allRestaurant,
            [locale]: {
              ...data,
              __updatedAt: Date.now()
            }
          }
        }
      },
      UPDATE_RESTAURANT(state, { data, id, locale }) {
        let allRestaurant = state.restaurants[id] || {};
        state.restaurants = {
          ...state.restaurants,
          [id]: {
            ...allRestaurant,
            [locale]: {
              ...data,
              __updatedAt: Date.now()
            }
          }
        }
      },
    },
    actions: {
      FETCH_INTRODUCTION({ state, commit }, { locale }) {
        if (state.recommends[locale]
          && state.__updatedAt[locale]
          && Date.now() - state.__updatedAt[locale] < cachetime 
        ) return ;

        return axios.get(encodeURI(`/api/dining`))
          .then((res) => {
            const data = res.data || {};
            const { recommend=[] } = data;

            commit('SET_RECOMMENDS', { data: recommend, locale });
          }).catch(err => console.log(err));      
      },
      FETCH_QTSAS_RESTAURANT_FILTER({ state, commit }, { locale }) {
        if (state.qtsasFilter[locale]
          && state.qtsasFilter[locale].data
          && state.qtsasFilter[locale].__updatedAt
          && Date.now() - state.qtsasFilter[locale].__updatedAt < cachetime 
        ) return ;

        return axios.get(encodeURI(`/api/dining/qtsas_restaurant_filter`))
          .then((res) => {
            const data = res.data || defaultFilter;
            commit('SET_FILTER', { filter: 'qtsasFilter', data, locale });
          }).catch(err => console.log(err));      
      },
      FETCH_RESTAURANT_FILTER({ state, commit }, { locale }) {
        if (state.restaurantFilter[locale]
          && state.restaurantFilter[locale].data
          && state.restaurantFilter[locale].__updatedAt
          && Date.now() - state.restaurantFilter[locale].__updatedAt < cachetime 
        ) return ;

        return axios.get(encodeURI(`/api/dining/restaurant_filter`))
          .then((res) => {
            const data = res.data || defaultFilter;
            commit('SET_FILTER', { filter: 'restaurantFilter', data, locale });
          }).catch(err => console.log(err));  
      },
      SEARCH_QTSAS_RESTAURANT({ state, commit }, { 
        district='', category='', cuisine='',
        keyword='', page=1, locale='en' 
      }) {
        if (district != state.qtsasSearch.district 
          || category != state.qtsasSearch.category 
          || cuisine != state.qtsasSearch.cuisine 
          || keyword != state.qtsasSearch.keyword 
          || locale != state.qtsasSearch.locale
        ) {
          commit('RESET_QTSAS_RESTAURANT_SEARCH', { 
            district, category, cuisine, keyword, locale
          })
        } else if (!state.qtsasSearch.results.slice(PAGE_SIZE * (page - 1), PAGE_SIZE * page).some(e => e == null)) {
          return ;
        }

        return axios.get(encodeURI(`/api/dining/qtsas_restaurant?lang=${locale}&district=${district}&category=${category}&cuisine=${cuisine}&keyword=${keyword}`))
          .then((res) => {
            let { resultCount=0, results=[] } = res.data;
            if (resultCount != state.qtsasSearch.resultCount) {
              commit('REFRESH_QTSAS_RESTAURANT_SEARCH_RESULT', { resultCount });
            }
            
            if (results.length > 0) {
              results.forEach(r => {
                if (r.awards) {
	                  r.awards.forEach(a => {
	                    if (a.icon) {
	                      const iconClass = String(a.icon).trim();
	                      const hasStyleClass = /\b(fa-(?:solid|regular|light|duotone|brands|thin)|fas|far|fal|fad|fab|fat)\b/.test(iconClass);
	                      const withFaFw = /\bfa-fw\b/.test(iconClass) ? iconClass : `fa-fw ${iconClass}`;
	                      a.icon = hasStyleClass ? withFaFw : `fa-fw fal ${iconClass}`;
	                    } else {
	                      a.icon = a.value == 1 
	                        ? 'fa-fw fal fa-file-certificate' 
                        : 'fa-fw fal fa-trophy-alt';
                      }
                    })
                }
              })
              commit('UPDATE_QTSAS_RESTAURANT_SEARCH_RESULT', { results });
            }
          }).catch(err => console.log(err));
      },
      SEARCH_RESTAURANT({ state, commit }, {
        cuisine='', category='', keyword='', page=1, locale='en' 
      }) {
        if (cuisine != state.search.cuisine 
          || keyword != state.search.keyword
          || category != state.search.category
          || locale != state.search.locale
        ) {
          commit('RESET_RESTAURANT_SEARCH', { cuisine, category, keyword, locale })
        } else if (!state.search.results.slice(PAGE_SIZE * (page - 1), PAGE_SIZE * page).some(e => e == null)) {
          return ;
        }

        return axios.get(encodeURI(`/api/dining/restaurant?lang=${locale}&cuisine=${cuisine}&category=${category}&keyword=${keyword}&page=${page}`))
          .then((res) => {
            let { resultCount=0, results=[] } = res.data;
            if (resultCount != state.search.resultCount) {
              commit('REFRESH_RESTAURANT_SEARCH_RESULT', { resultCount });
            }
            
            if (results.length > 0) {
              commit('UPDATE_RESTAURANT_SEARCH_RESULT', { page, results });
            }
          }).catch(err => console.log(err));
      },
      FETCH_QTSAS_RESTAURANT({ state, commit, dispatch }, { id='', locale='en', ssr }) {
        if (state.qtsasRestaurants[id]
          && state.qtsasRestaurants[id][locale]
          && state.qtsasRestaurants[id][locale].__updatedAt
          && Date.now() - state.qtsasRestaurants[id][locale].__updatedAt < cachetime 
        ) {
          return ;
        }

        return axios.get(encodeURI(`/api/dining/qtsas_restaurant/${id}`))
          .then((res) => {
            const data = res.data || {};
            delete data.pois; // old api still contains pois field, delete to avoid vue reactivity issue
            // data.pois = [];
            // console.log('datapois: ', data.pois)
            const entityCoordinates = data.coordinate || [];
            const coordinate = Array.isArray(entityCoordinates[0]) ? entityCoordinates[0].join('|') : entityCoordinates.join('|');

            commit('UPDATE_QTSAS_RESTAURANT', {
              data,
              id,
              locale
            })

            // ssr no FETCH_POI
            if (ssr) {
              return;
            } else if (coordinate) {
              return dispatch('poi/FETCH_POI', { coordinate, locale })
                .then((pois) => {
                  data.pois = pois.filter(poi => poi.id !== data.id).map((poi) => {
                    return Object.assign(poi, {
                      distance: distanceFromSightToPoi(data, poi),
                    });
                  });

                  commit('UPDATE_QTSAS_RESTAURANT', {
                    data,
                    id,
                    locale
                  })
                })
                .catch(err => {
                  console.log(err);
                  // if (err.response && err.response.status == 404) {
                  throw err;
                  // }
                });
            } else {
              console.warn(id, 'missing coordinate data')
            }
          }).catch(err => {
            console.log(err);
            // if (err.response && err.response.status == 404) {
              throw err;
            // }
          })
      },
      FETCH_RESTAURANT({ state, commit, dispatch }, { id='', locale='en', ssr }) {
        if (state.restaurants[id]
          && state.restaurants[id][locale]
          && state.restaurants[id][locale].__updatedAt
          && Date.now() - state.restaurants[id][locale].__updatedAt < cachetime 
        ) {
          return ;
        }

        return axios.get(encodeURI(`/api/dining/restaurant/${id}`))
          .then((res) => {
            const data = res.data || {};
            delete data.pois; // old api still contains pois field, delete to avoid vue reactivity issue
            // data.pois = [];
            // console.log('datapois: ', data.pois)
            const entityCoordinates = data.coordinate || [];
            const coordinate = Array.isArray(entityCoordinates[0]) ? entityCoordinates[0].join('|') : entityCoordinates.join('|');

            commit('UPDATE_RESTAURANT', {
              data,
              id,
              locale
            })

            // ssr no FETCH_POI
            if (ssr) {
              return;
            } else if (coordinate) {
              return dispatch('poi/FETCH_POI', { coordinate, locale })
                .then((pois) => {
                  data.pois = pois.filter(poi => poi.id !== data.id).map((poi) => {
                    return Object.assign(poi, {
                      distance: distanceFromSightToPoi(data, poi),
                    });
                  });

                  commit('UPDATE_RESTAURANT', {
                    data,
                    id,
                    locale
                  })
                })
                .catch(err => {
                  console.log(err);
                  // if (err.response && err.response.status == 404) {
                  throw err;
                  // }
                });
            } else {
              console.warn(id, 'missing coordinate data')
            }
          }).catch(err => {
            console.log(err);
            // if (err.response && err.response.status == 404) {
              throw err;
            // }
          })
      },
    },
    getters: { 
      getQtsasSearchResult: (state, getters, rootState) => () => {
        let raw = [...state.qtsasSearch.results] || [];
        const { isGeolocationOn, userLocation } = rootState.geolocation;

        if (raw && isGeolocationOn) {
          // sort raw
          raw.sort((a, b) => getSightDistance(a, userLocation) - getSightDistance(b, userLocation));
        }

        return raw;
      },
      getExistSearchResult: (state, getters, rootState) => (
        id, options, packAsGroup=false
      ) => {
        // check options match
        let {
          district='', category='', cuisine='',
          keyword='', locale='en' 
        } = options;

        if (district != state.qtsasSearch.district 
          || category != state.qtsasSearch.category 
          || cuisine != state.qtsasSearch.cuisine 
          || keyword != state.qtsasSearch.keyword 
          || locale != state.qtsasSearch.locale
        ) {
          return []
        }

        // check target exist
        if (!state.qtsasSearch.results.find(o => o.id == id)) {
          return []
        }

        // return result
        const results = getters.getQtsasSearchResult(locale);
        return packAsGroup 
          ? {
            sights: results
          }
          : results; 
      },
      getRecommends: (state) => (locale) => {
        return state.recommends[locale] || [];
      },
      getQtsasFilter: (state) => (locale) => {
        return state.qtsasFilter[locale] 
          ? (state.qtsasFilter[locale].data || defaultFilter) 
          : defaultFilter;
      },
      getRestaurantFilter: (state) => (locale) => {
        return state.restaurantFilter[locale] 
          ? (state.restaurantFilter[locale].data || defaultFilter) 
          : defaultFilter;
      },
      getQtsasRestaurant: (state) => (id, locale) => {
        let data = (state.qtsasRestaurants[id] && state.qtsasRestaurants[id][locale]) 
          || {
            gallery: []
          };

        data.filters = [];
        data.category && data.filters.push({
          name: data.category.name,
          to: `/dining/qtsas-restaurant?category=${data.category.value}`
        })

        if (data.cuisine) 
          data.cuisine.to = `/dining/qtsas-restaurant?cuisine=${data.cuisine.value}`;

        if (data.awards) {
          data.awardAsessmentYears = [
            // ['xxx', [2019, 2020]],
            // ['xxxx', [2022]],
            // ...
          ];
          data.awardRemarks = [];

          data.awards.forEach(a => {
            if (!a.icon) {
              a.icon = a.value == 1 
                ? 'fa-fw fas fa-file-certificate' 
                : 'fa-fw fas fa-trophy-alt';
            }
            if (a.remark) {
              data.awardRemarks.push(a.remark);
            }
          })

          const awardsWithValidity = data.awards.filter(a => Array.isArray(a.validity) && a.validity.length > 0) || [];
          awardsWithValidity.forEach(a => {
            a.validity.forEach(y => {
              const award = data.awardAsessmentYears.find(([awardName, asessmentYears]) => awardName === a.name);
              if (award) {
                if (!award[1].includes(y)) {
                  award[1].push(y);
                }
              } else {
                data.awardAsessmentYears.push([a.name, [y]])
              }
            });
          });

          let awardWithValidity = awardsWithValidity[0] || data.awards.find(a => Array.isArray(a.validity));
          if (awardWithValidity) {
            let {infos={}} = data;

            infos.awardValidity = awardWithValidity.validity
              .map(year => parseInt(year, 10) || 0)
              .filter(year => year > 0)
              .map(year => `${year}-${year+1}`)
              .join('; ');
          }
        }

        return data;
      },
      getRestaurant: (state) => (id, locale) => {
        let data =  (state.restaurants[id] && state.restaurants[id][locale]) 
          || {
            gallery: []
          };

        data.filters = [];
        if (data.cuisine) 
          data.cuisine.to = `/dining/qtsas-restaurant?cuisine=${data.cuisine.value}`;

        return data;
      }
    },
    modules: {
      poi: poi(),
    },
  }
}
